import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { normalizeOauthService } from '../api';
import { settingsPath } from '../routes';
import { useGrowthSettingsMutations } from '../hooks/useGrowthSettings';

export function SettingsOauthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { completeOauth } = useGrowthSettingsMutations();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const code = params.get('code') ?? '';
  const state = params.get('state') ?? '';
  const providerParam = params.get('provider') ?? params.get('service') ?? '';

  const service = useMemo(() => {
    const fromParam = normalizeOauthService(providerParam);
    if (fromParam) return fromParam;
    const stored = sessionStorage.getItem('growth_oauth_service');
    return stored ? normalizeOauthService(stored) : null;
  }, [providerParam]);

  useEffect(() => {
    if (startedRef.current) return;
    if (!code || !state || !service) {
      setError('Missing OAuth callback parameters.');
      return;
    }

    startedRef.current = true;
    completeOauth.mutate(
      { service, code, state },
      {
        onSuccess: () => {
          sessionStorage.removeItem('growth_oauth_service');
          sessionStorage.removeItem('growth_oauth_state');
          navigate(settingsPath('connections'), { replace: true });
        },
        onError: (e) => {
          setError(e instanceof Error ? e.message : 'OAuth callback failed.');
        },
      },
    );
  }, [code, state, service, completeOauth, navigate]);

  return (
    <AsyncBoundary
      loading={completeOauth.isPending && !error}
      error={error ?? undefined}
      onRetry={() => window.location.reload()}
    >
      <div className="mx-auto max-w-lg px-6 py-16 text-center text-[14px] text-[var(--ink-2)]">
        Connection complete. Redirecting to settings…
      </div>
    </AsyncBoundary>
  );
}
